import { useEffect, useState } from 'react';

import { BREAKPOINT_NAMES, BREAKPOINTS, type Breakpoint } from '../../shared';

export interface BreakpointInfo {
  breakpoint: Breakpoint | 'base';
  above: (name: Breakpoint) => boolean;
  below: (name: Breakpoint) => boolean;
  between: (min: Breakpoint, max: Breakpoint) => boolean;
  is: (name: Breakpoint | 'base') => boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const DEBOUNCE_MS = 100;

function widthNow(): number {
  return typeof window === 'undefined' ? BREAKPOINTS.lg : window.innerWidth;
}

function resolve(width: number): BreakpointInfo {
  const above = (name: Breakpoint) => width >= BREAKPOINTS[name];
  const below = (name: Breakpoint) => width < BREAKPOINTS[name];
  const between = (min: Breakpoint, max: Breakpoint) => above(min) && below(max);

  let breakpoint: Breakpoint | 'base' = 'base';
  for (const name of BREAKPOINT_NAMES) {
    if (above(name)) breakpoint = name;
  }

  return {
    breakpoint,
    above,
    below,
    between,
    is: (name) => breakpoint === name,
    isMobile: below('md'),
    isTablet: between('md', 'lg'),
    isDesktop: above('lg'),
  };
}

export function useBreakpoint(): BreakpointInfo {
  const [info, setInfo] = useState(() => resolve(widthNow()));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        setInfo(resolve(window.innerWidth));
      }, DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (timer !== null) clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return info;
}
